import { useEffect, useState } from 'react';

/**
 * Returns `true` only after `active` has been continuously true for `delayMs`.
 * Used to avoid flashing a loading indicator on fast responses: a load that
 * resolves under ~250ms never shows a spinner/skeleton at all. If `active` goes
 * false before the delay elapses, the flag never trips.
 *
 * @param active   whether the underlying operation is in progress
 * @param delayMs  how long to wait before showing the loader (default 250ms)
 */
export function useDelayedFlag(active: boolean, delayMs = 250): boolean {
  const [shown, setShown] = useState(false);

  useEffect(() => {
    if (!active) {
      // Reset asynchronously so we don't setState synchronously inside the
      // effect (which can trigger cascading renders). A microtask is enough.
      const reset = setTimeout(() => setShown(false), 0);
      return () => clearTimeout(reset);
    }
    const id = setTimeout(() => setShown(true), delayMs);
    return () => clearTimeout(id);
  }, [active, delayMs]);

  return shown;
}
